import { IS_BP_MANAGER, PRODUCTION_HOSTNAMES } from "@/consts";
import { LocalStorageKeys } from "@/enums";
import { useAuthStore } from "@/stores/auth.module";
import { ref } from "vue";

type MatomoStatus = "none" | "cancelled" | "ready";

export type MatomoCategories =
  | "Activity.DetailView"
  | "Activity.ListView"
  | "Meeting.DetailView"
  | "Meeting.ListView"
  | "News.DetailView"
  | "News.ListView"
  | "Meal.DetailView"
  | "Meal.ListView"
  | "Planner.DayView"
  | "Planner.WeekView"
  | "Planner.ListView"
  | "Planner.Menus"
  | "Planner.EditTask"
  | "Dashboard.CalendarWidget"
  | "Dashboard.CoreNewsWidget"
  | "Dashboard.SupportWidget"
  | "Dashboard.WorkSchedule"
  | "Navigation"
  | "Search.Global";

export type MatomoPageViewTitles =
  | "Activities"
  | "Activities/Item"
  | "Activities/CoreData"
  | "Activities/Notifications"
  | "Activities/Participants"
  | "Activities/PDF"
  | "Activities/Pictures"
  | "Activities/Video"
  | "Activities/Categories"
  | "Admin/CoreNews"
  | "Admin/CoreNews/Item"
  | "Admin/Scripts"
  | "Dashboard"
  | "Dashboard/CoreNewsItem"
  | "Meals"
  | "Meals/NewItemDialog"
  | "Meals/CoreData"
  | "Meals/Pictures"
  | "Meals/PDF"
  | "Meals/Participants"
  | "Meals/Meals/Item"
  | "Meals/Dishes"
  | "Meals/Dishes/CoreData"
  | "Meals/Dishes/Pictures"
  | "Meals/Dishes/Item"
  | "Meeting/Agendas"
  | "Meeting/Agendas/CoreData"
  | "Meeting/Agendas/Pictures"
  | "Meeting/Agendas/Item"
  | "Meeting/Meetings"
  | "Meeting/Meetings/CoreData"
  | "Meeting/Meetings/Notifications"
  | "Meeting/Meetings/Participants"
  | "Meeting/Meetings/Pictures"
  | "Meeting/Meetings/Suggestions"
  | "Meeting/Meetings/Item"
  | "News"
  | "News/Item"
  | "News/CoreData"
  | "News/Notifications"
  | "News/Participants"
  | "News/PDF"
  | "News/Pictures"
  | "News/Video"
  | "News/Categories"
  | "Planner"
  | "Planner/DayView"
  | "Planner/WeekView"
  | "Support";

const status = ref<MatomoStatus>("none");

const DIMENSION_INSTITUTION = 1;

export const init = (): void => {
  if (status.value !== "none") {
    return;
  }

  if (
    IS_BP_MANAGER ||
    !PRODUCTION_HOSTNAMES.includes(window.location.hostname)
  ) {
    status.value = "cancelled";
    return;
  }

  const _paq = (window._paq = window._paq || []);
  (function () {
    const u = "//aws.proreact.dk/tracking/";
    _paq.push(["enableLinkTracking"]);
    _paq.push(["setTrackerUrl", u + "matomo.php"]);
    _paq.push(["setSiteId", "13"]);

    const d = document;
    const g = d.createElement("script");
    const s = d.getElementsByTagName("script")[0];

    g.type = "text/javascript";
    g.async = true;
    g.defer = true;
    g.src = u + "matomo.js";

    if (s && s.parentNode) {
      s.parentNode.insertBefore(g, s);
    }
  })();

  status.value = "ready";
};

export function useMatomo() {
  init();
  const authStore = useAuthStore();
  const _paq = (window._paq = window._paq || []);

  const trackEvent = (
    action: string,
    category: MatomoCategories,
    extraInfo?: string,
  ) => {
    if (localStorage.getItem(LocalStorageKeys.ActivateMatomoLogs)) {
      // oxlint-disable-next-line no-console
      console.log(
        `📊 Category: ${category}\n🎯 Action: ${action}\n📝 Extra Info: ${extraInfo || "(none)"}\n🏢 Institution: ${authStore.institutionId}`,
      );
    }

    if (status.value !== "ready") {
      return;
    }

    _paq.push([
      "setCustomDimension",
      DIMENSION_INSTITUTION,
      authStore.institutionId,
    ]);
    _paq.push(["trackEvent", category, action, extraInfo || ""]);
  };

  const trackPageView = (title: MatomoPageViewTitles) => {
    if (status.value !== "ready") {
      return;
    }

    const cleanUrl = new URL(window.location.href);
    cleanUrl.search = "";

    _paq.push([
      "setCustomDimension",
      DIMENSION_INSTITUTION,
      authStore.institutionId,
    ]);
    _paq.push(["setDocumentTitle", title]);
    _paq.push(["setCustomUrl", cleanUrl.toString()]);
    _paq.push(["trackPageView"]);
  };

  return {
    trackEvent,
    trackPageView,
  };
}
